import { ReactNode } from "react";

interface CloudBackgroundProps {
  children?: ReactNode;
  className?: string;
}

const CloudBackground = ({ children, className = "" }: CloudBackgroundProps) => {
  return (
    <section className={`relative min-h-screen bg-gradient-sky ${className}`}>
      <style>{`
        .bg-gradient-sky {
          background: linear-gradient(
            180deg,
            hsl(188 80% 65%) 0%,
            hsl(195 100% 55%) 30%,
            hsl(200 100% 50%) 60%,
            hsl(175 80% 50%) 100%
          );
        }
        .bg-sun-glow {
          background: radial-gradient(
            circle,
            hsl(55 100% 85%) 0%,
            hsl(55 100% 75% / 0.5) 50%,
            transparent 70%
          );
        }
      `}</style>
      {/* Fade-out mask for edges - minimal on mobile */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top fade */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white via-white/50 to-transparent" />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white via-white/50 to-transparent" />
        {/* Left fade - minimal on mobile (4px), normal on desktop (32px = 8rem) */}
        <div className="absolute top-0 left-0 w-1 md:w-32 h-full bg-gradient-to-r from-white via-white/30 to-transparent" />
        {/* Right fade - minimal on mobile (4px), normal on desktop */}
        <div className="absolute top-0 right-0 w-1 md:w-32 h-full bg-gradient-to-l from-white via-white/30 to-transparent" />
      </div>
      {/* Cloud effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Top edge clouds */}
        <div className="absolute -top-10 left-0 w-full h-48 bg-gradient-to-b from-white/60 to-transparent" />
        <div className="absolute -top-5 left-0 w-[40%] h-40 bg-white/50 rounded-full blur-3xl" />
        <div className="absolute top-0 left-[10%] w-72 h-32 bg-white/60 rounded-full blur-2xl" />
        <div className="absolute -top-8 left-[25%] w-48 h-28 bg-white/70 rounded-full blur-xl" />
        <div className="absolute top-5 right-[15%] w-80 h-36 bg-white/40 rounded-full blur-3xl" />
        <div className="absolute -top-5 right-[5%] w-64 h-32 bg-white/55 rounded-full blur-2xl" />
        <div className="absolute top-0 right-0 w-[35%] h-36 bg-white/45 rounded-full blur-3xl" />
        {/* Middle scattered clouds */}
        <div className="absolute top-20 left-[5%] w-40 h-20 bg-white/25 rounded-full blur-2xl" />
        <div className="absolute top-16 right-[10%] w-48 h-24 bg-white/20 rounded-full blur-3xl" />
        {/* Bottom edge clouds */}
        <div className="absolute -bottom-10 left-0 w-full h-48 bg-gradient-to-t from-white/60 to-transparent" />
        <div className="absolute -bottom-5 left-[5%] w-80 h-40 bg-white/55 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-[20%] w-64 h-32 bg-white/50 rounded-full blur-2xl" />
        <div className="absolute -bottom-8 left-[40%] w-56 h-28 bg-white/60 rounded-full blur-xl" />
        <div className="absolute bottom-5 right-[25%] w-72 h-36 bg-white/45 rounded-full blur-3xl" />
        <div className="absolute -bottom-5 right-[8%] w-[17rem] h-[8.5rem] bg-white/55 rounded-full blur-2xl" />
        <div className="absolute bottom-0 right-0 w-[30%] h-40 bg-white/50 rounded-full blur-3xl" />
      </div>
      {/* Sun glow */}
      <div className="absolute top-0 right-1/4 w-48 h-48 bg-sun-glow rounded-full blur-3xl opacity-80" />
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </section>
  );
};

export default CloudBackground;